import { FILE_PURPOSES } from "@station/contracts";
import { Alert, Button, Card, Label, PageHeader, SkeletonRows, Textarea, useToast } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ImageUp, Trash2 } from "lucide-react";
import { type ReactElement, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReportSettings, useSaveReportSettings } from "../../../features/reports/queries.js";
import { ReportsGate } from "../../../features/reports/shared.js";
import { uploadFile } from "../../../features/station-import.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

export const Route = createFileRoute("/_app/raporlar/ayarlar")({ component: ReportSettingsRoute });

const LOGO_MAX_BYTES = FILE_PURPOSES.REPORT_LOGO.maxBytes;
const LOGO_TYPES: readonly string[] = FILE_PURPOSES.REPORT_LOGO.mimeTypes;

/**
 * Rapor ayarları (Prompt 22, ADR 0022 §13): PDF raporların antedindeki kiracı logosu (PNG / JPEG, en çok 1 MB; yoksa kiracı
 * adı yazılır) ve altbilgi notu. Değişiklik denetim izine yazılır. `reports.settings.manage` (sahip / yönetici).
 */
function ReportSettingsRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsSettings"));
  return (
    <>
      <PageHeader title={t("menu.reportsSettings")} description={t("reports.settings.description")} />
      <ReportsGate need="settings">
        <ReportSettingsForm />
      </ReportsGate>
    </>
  );
}

function ReportSettingsForm(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const settings = useReportSettings();
  const save = useSaveReportSettings();
  const inputRef = useRef<HTMLInputElement>(null);
  const footerId = useId();
  const logoHintId = useId();
  const [footerNote, setFooterNote] = useState("");
  const [logoFileId, setLogoFileId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setFooterNote(settings.data.footerNote ?? "");
    setLogoFileId(settings.data.logoFileId);
    setPreview(settings.data.logoUrl);
  }, [settings.data]);

  async function chooseLogo(file: File): Promise<void> {
    setError(null);
    if (!LOGO_TYPES.includes(file.type)) {
      setError(t("reports.settings.logoType"));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError(t("reports.settings.logoSize"));
      return;
    }
    setUploading(true);
    try {
      const id = await uploadFile(file, "REPORT_LOGO");
      setLogoFileId(id);
      setPreview(URL.createObjectURL(file));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setUploading(false);
    }
  }

  async function submit(): Promise<void> {
    if (!settings.data) return;
    setError(null);
    try {
      await save.mutateAsync({
        logoFileId,
        footerNote: footerNote.trim() ? footerNote.trim() : null,
        version: settings.data.version,
      });
      toast({ tone: "success", title: t("reports.settings.saved") });
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  if (settings.error) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;
  if (!settings.data) return <SkeletonRows rows={4} />;
  const dirty =
    logoFileId !== settings.data.logoFileId ||
    (footerNote.trim() || null) !== (settings.data.footerNote ?? null);

  return (
    <Card className="grid max-w-2xl gap-6 p-6">
      <form
        className="grid gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <section className="grid gap-3" aria-labelledby="report-logo-title">
          <h2 id="report-logo-title" className="text-base font-semibold">
            {t("reports.settings.logoTitle")}
          </h2>
          <p id={logoHintId} className="text-sm text-muted-foreground">
            {t("reports.settings.logoHint")}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex h-24 w-72 items-center justify-center rounded-md border bg-white p-2">
              {preview ? (
                <img
                  src={preview}
                  alt={t("reports.settings.logoPreview")}
                  className="max-h-20 max-w-full object-contain"
                />
              ) : (
                <span className="text-xs text-slate-600">{t("reports.settings.noLogo")}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={inputRef}
                type="file"
                accept={LOGO_TYPES.join(",")}
                className="sr-only"
                aria-describedby={logoHintId}
                aria-label={t("reports.settings.chooseLogo")}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void chooseLogo(file);
                }}
              />
              <Button variant="outline" onClick={() => inputRef.current?.click()} loading={uploading}>
                <ImageUp aria-hidden="true" />
                {t("reports.settings.chooseLogo")}
              </Button>
              {logoFileId ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setLogoFileId(null);
                    setPreview(null);
                  }}
                >
                  <Trash2 aria-hidden="true" />
                  {t("reports.settings.removeLogo")}
                </Button>
              ) : null}
            </div>
          </div>
        </section>
        <div className="grid gap-1.5">
          <Label htmlFor={footerId}>{t("reports.settings.footerNote")}</Label>
          <Textarea
            id={footerId}
            value={footerNote}
            maxLength={200}
            rows={2}
            onChange={(event) => setFooterNote(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t("reports.settings.footerHint")}</p>
        </div>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {settings.data.updatedAt
              ? t("reports.settings.updatedAt", { at: formatDateTime(settings.data.updatedAt) })
              : t("reports.settings.neverSaved")}
          </p>
          <Button type="submit" loading={save.isPending} disabled={!dirty || uploading}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
